import React, { useState, useEffect, useCallback } from "react";
import { HelpCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

interface TutorialStep {
  target: string;
  title: string;
  content: string;
  position: "top" | "right" | "bottom" | "left";
  offset?: {
    x?: number;
    y?: number;
  };
}

interface TooltipProps {
  step: TutorialStep;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onClose: () => void;
}

interface HighlightProps {
  step: TutorialStep;
}

interface TutorialWalkthroughProps {
  steps: TutorialStep[];
  onComplete?: () => void;
  buttonContent?: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onClose,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const calculatePosition = useCallback(() => {
    const targetElement = document.querySelector(step.target);
    if (!targetElement) return null;

    const rect = targetElement.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 180;
    const padding = {
      top: 20,
      right: 20,
      bottom: 20,
      left: 0,
    };

    let top = 0;
    let left = 0;

    switch (step.position) {
      case "bottom":
        top = rect.bottom + padding.bottom;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case "right":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding.right;
        break;
      case "left":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding.left;
        break;
      case "top":
        top = rect.top - tooltipHeight - padding.top;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
    }

    // Apply custom offsets if provided
    if (step.offset) {
      if (typeof step.offset.x === "number") left += step.offset.x;
      if (typeof step.offset.y === "number") top += step.offset.y;
    }

    // Adjust for viewport boundaries
    // Viewport boundary adjustment happens after offset is applied
    const finalTop = Math.max(
      padding.top,
      Math.min(top, window.innerHeight - tooltipHeight - padding.bottom)
    );
    const finalLeft = Math.max(
      padding.left,
      Math.min(left, window.innerWidth - tooltipWidth - padding.right)
    );

    return { top: finalTop, left: finalLeft };
  }, [step]);

  const updatePosition = useCallback(() => {
    const newPosition = calculatePosition();
    if (newPosition) {
      setPosition(newPosition);
    }
  }, [calculatePosition]);

  useEffect(() => {
    updatePosition();

    const handleResize = () => {
      requestAnimationFrame(updatePosition);
    };

    const handleScroll = () => {
      requestAnimationFrame(updatePosition);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    const observer = new MutationObserver(() => {
      requestAnimationFrame(updatePosition);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
      observer.disconnect();
    };
  }, [updatePosition]);

  useEffect(() => {
    updatePosition();
  }, [step, updatePosition]);

  return (
    <Card
      className="fixed w-96 z-50 bg-white shadow-xl rounded-lg"
      style={{
        transform: `translate3d(${position.left}px, ${position.top}px, 0px)`,
      }}
    >
      <CardHeader className="bg-indigo-700 rounded-t-lg pl-4 pr-2 py-1 flex flex-row justify-between items-center">
        <CardTitle className="text-[15px] text-white">{step.title}</CardTitle>
        <Button
          variant="ghost"
          size="icon"
          className="hover:bg-indigo-600/50 rounded-md transition-colors h-10 w-10 pb-1 flex items-center justify-center"
          onClick={onClose}
        >
          <i className="fa-solid fa-xmark text-white text-md pointer-events-none" />
        </Button>
      </CardHeader>

      <CardContent className="p-4 pt-3">
        <p className="text-[13px] text-stone-700 leading-relaxed">
          {step.content}
        </p>
      </CardContent>

      <CardFooter className="p-3 pt-0 flex justify-between items-center bg-gray-50/50">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrevious}
          disabled={currentStep === 0}
          className="border-primary/20 hover:bg-primary/5"
        >
          Previous
        </Button>

        <span className="text-sm text-gray-500 font-medium">
          {currentStep + 1} of {totalSteps}
        </span>

        <Button
          size="sm"
          onClick={onNext}
          className="bg-indigo-800 hover:bg-indigo-800/90"
        >
          {currentStep === totalSteps - 1 ? "Finish" : "Next"}
        </Button>
      </CardFooter>
    </Card>
  );
};

const Highlight: React.FC<HighlightProps> = ({ step }) => {
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const updatePosition = useCallback(() => {
    const targetElement = document.querySelector(step.target);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setPosition({
        top: rect.top - 4,
        left: rect.left - 4,
        width: rect.width + 8,
        height: rect.height + 8,
      });
    }
  }, [step]);

  useEffect(() => {
    updatePosition();

    const handleResize = () => {
      requestAnimationFrame(updatePosition);
    };

    const handleScroll = () => {
      requestAnimationFrame(updatePosition);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    const observer = new MutationObserver(() => {
      requestAnimationFrame(updatePosition);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
      observer.disconnect();
    };
  }, [updatePosition]);

  return (
    <div
      className="fixed border-2 border-primary/30 rounded bg-primary/5 transition-all duration-200 z-40"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        height: position.height,
      }}
    />
  );
};

const TutorialWalkthrough: React.FC<TutorialWalkthroughProps> = ({
  steps,
  onComplete,
  buttonContent,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!isActive) return;

      switch (event.key) {
        case "Escape":
          endTutorial();
          break;
        case "ArrowRight":
        case "Enter":
          nextStep();
          break;
        case "ArrowLeft":
          previousStep();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isActive, currentStep]);

  const startTutorial = () => {
    setIsActive(true);
    setCurrentStep(0);
    document.body.style.overflow = "hidden";
  };

  const endTutorial = () => {
    setIsActive(false);
    setCurrentStep(0);
    document.body.style.overflow = "auto";
    onComplete?.();
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      endTutorial();
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  if (!steps?.[currentStep]) {
    return null;
  }

  return (
    <>
      <button
        onClick={startTutorial}
        className="flex items-center justify-center w-8 h-8 2xl:w-10 2xl:h-10 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-full transition-all duration-300 ease-in-out hover:scale-[1.05]"
      >
        <QuestionMarkCircleIcon className="w-4 h-4 2xl:w-5 2xl:h-5" />
      </button>

      {isActive && (
        <>
          <div className="fixed inset-0 bg-background/80 z-30" />
          <Highlight step={steps[currentStep]} />
          <Tooltip
            step={steps[currentStep]}
            currentStep={currentStep}
            totalSteps={steps.length}
            onNext={nextStep}
            onPrevious={previousStep}
            onClose={endTutorial}
          />
        </>
      )}
    </>
  );
};

export default TutorialWalkthrough;
